import { ChangeEvent, ReactNode, useState } from 'react';

import {
  AutoComplete,
  ChakraBox,
  ChakraFlex,
  ChakraMenuList,
  ChakraSimpleGrid,
  ChakraText,
  CheckboxGroup,
  Close,
  DatePicker,
  EquipUIFireflyV2Theme,
  FireflyButton,
  Flag,
  MenuSelect,
  MenuSelectOption,
  Search,
  TextInput,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { useSearchData } from '~/components/common/navbar/GlobalSearch/useSearchData';
import { toPatientSuggestion } from '~/components/common/navbar/GlobalSearch/utils';
import { PATIENT_SEARCH_PAGE_SIZE } from '~/lib/constants/patients.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  UNASSIGNED_FILTER_OPTION,
  UNASSIGNED_PATIENT_OPTION,
} from '~/lib/constants/user.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  getTaskParamsFromFilters,
  taskPriorityDefinitions,
  taskStatusDefinitions,
} from '~/lib/util/task.util';
import {
  getProviderAutocompleteOption,
  getUserOption,
} from '~/lib/util/user.util';
import { userOptionAlphabeticalSortCompareFunction } from '~/lib/utils';
import {
  useGetAllProvidersV2Query,
  useLazyGetAllProvidersV2Query,
} from '~/store/services/providers';

const { primary } = EquipUIFireflyV2Theme.colors;
const GET_PROVIDERS_PAGE_SIZE = 25 as const;

export interface TaskFiltersProps {
  categories: AutoCompleteOption[];
  filters: TTaskFilters;
  getTasks: (params: Record<string, string | string[]>) => void;
  handleClose: () => void;
  isOpen: boolean;
}

interface FlagLabelProps {
  children: ReactNode;
  fill: string;
}

const FlagLabel = ({ children, fill }: FlagLabelProps) => (
  <ChakraBox alignItems="baseline" as="span" display="flex">
    <Flag fill={fill} height="12px" width="12px" />
    &nbsp; {children}
  </ChakraBox>
);

const DATE_DISPLAY_FORMAT = 'LL/dd/yyyy';

const DATE_VALUE_FORMAT = `${DATE_DISPLAY_FORMAT} HH:mm:ss`;

const TOP_LEVEL_ID = 'task-filter';

const TaskFilterMenu = ({
  categories,
  filters,
  getTasks,
  handleClose,
  isOpen,
}: TaskFiltersProps) => {
  const [tempFilters, setTempFilters] = useState<
    Record<keyof TTaskFilters, AutoCompleteOption[]>
  >(
    Object.fromEntries(
      Object.entries(filters ?? {}).map(
        ([filterKey, { value }]: [keyof TTaskFilters, FilterType]) => [
          filterKey,
          value,
        ],
      ),
    ) as Record<keyof TTaskFilters, AutoCompleteOption[]>,
  );

  const {
    data: initialProviderData,
    isFetching: isGetInitialProvidersFetching,
  } = useGetAllProvidersV2Query(
    { 'page-size': GET_PROVIDERS_PAGE_SIZE, 'page-number': 1 },
    { skip: !isOpen },
  );

  const [
    getAssigneeSearchProviders,
    { data: assigneeProviders, isFetching: isGetAssigneeProvidersFetching },
  ] = useLazyGetAllProvidersV2Query();

  const [
    getCreatorSearchProviders,
    { data: creatorProviders, isFetching: isGetCreatorProvidersFetching },
  ] = useLazyGetAllProvidersV2Query();

  const priorities: SelectOption[] = taskPriorityDefinitions.map(
    ({ title, color }) => ({
      label: <FlagLabel fill={color}>{title}</FlagLabel>,
      value: title,
    }),
  );

  const getInitialDate = (dateString: string): Date => {
    return dateString === undefined ? null : new Date(dateString);
  };

  const [createdFromDate, setCreatedFromDate] = useState(
    getInitialDate(tempFilters?.createdDateFrom?.[0]?.value),
  );
  const [createdToDate, setCreatedToDate] = useState(
    getInitialDate(tempFilters?.createdDateTo?.[0]?.value),
  );
  const [dueFromDate, setDueFromDate] = useState(
    getInitialDate(tempFilters?.dueDateFrom?.[0]?.value),
  );
  const [dueToDate, setDueToDate] = useState(
    getInitialDate(tempFilters?.dueDateTo?.[0]?.value),
  );

  /** Add or replace a particular task filter */
  const handleAdd =
    (key: keyof TTaskFilters, shouldReplace?: boolean) =>
    (value: AutoCompleteOption | AutoCompleteOption[]): void =>
      setTempFilters({
        ...tempFilters,
        [key]: shouldReplace
          ? (value as AutoCompleteOption[])
          : tempFilters[key].concat(value),
      });

  /** Remove a task filter */
  const handleRemove =
    (key: keyof TTaskFilters) =>
    (option: AutoCompleteOption): void =>
      setTempFilters({
        ...tempFilters,
        [key]: tempFilters[key].filter(({ value }) => value !== option.value),
      });

  const handlePrioritiesSelect = (value: string[]): void => {
    handleAdd(
      'priority',
      true,
    )(
      value.map((val: string) => ({
        primaryText: taskPriorityDefinitions.find(({ title }) => title === val)
          .title,
        value: val,
      })),
    );
  };

  const handleStatusSelect = (
    value: MenuSelectOption | MenuSelectOption[],
  ): void => {
    handleAdd(
      'status',
      true,
    )(
      [].concat(value).map((val: MenuSelectOption) => ({
        primaryText: val.node as string,
        value: val.value,
      })),
    );
  };

  const handleChangeTitleFilter = (
    e?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { value } = e?.target ?? {};
    const options: AutoCompleteOption[] = value
      ? [
          {
            primaryText: value,
            value,
          },
        ]
      : [];
    handleAdd('title', true)(options);
  };

  const handleDateSelect = (
    date: Date,
    filterKey: keyof TTaskFilters,
    labelPrefix: string,
    isStartOfDay: boolean,
  ): void => {
    if (!date) return;
    let dateTime: DateTime = DateTime.fromJSDate(date).startOf('day');
    if (!isStartOfDay) dateTime = dateTime.endOf('day');

    handleAdd(
      filterKey,
      true,
    )([
      {
        primaryText: `${labelPrefix}: ${dateTime.toFormat(
          DATE_DISPLAY_FORMAT,
        )}`,
        value: dateTime.toFormat(DATE_VALUE_FORMAT),
      },
    ]);
  };

  const patients = useSearchData<Patient>({
    baselineTotalCount: 1,
    fetchUrl: urlConstants.users.getPatients,
    filterKey: 'patient-search',
    pageSize: PATIENT_SEARCH_PAGE_SIZE,
    toAutoCompleteOptionMapper: toPatientSuggestion,
  });

  const getDateValue = (date: string | Date): Date => {
    return date ? new Date(date) : null;
  };

  const { currentUser } = useGlobalContext();

  const selfOption = {
    ...getUserOption(true)(currentUser),
    isMe: true,
  };

  const assigneeProviderOptions: AutoCompleteOption[] = [
    UNASSIGNED_FILTER_OPTION,
    ...(selfOption ? [selfOption] : []),
    ...(assigneeProviders?.data || initialProviderData?.data || [])
      .filter(
        ({ userId }: GetProviderByIdResponse) => userId !== currentUser.userId,
      )
      .map((provider) => getProviderAutocompleteOption(provider))
      .sort(userOptionAlphabeticalSortCompareFunction),
  ];

  const creatorProviderOptions: AutoCompleteOption[] = [
    ...(selfOption ? [selfOption] : []),
    ...(creatorProviders?.data || initialProviderData?.data || [])
      .filter(
        ({ userId }: GetProviderByIdResponse) => userId !== currentUser.userId,
      )
      .map((provider) => getProviderAutocompleteOption(provider))
      .sort(userOptionAlphabeticalSortCompareFunction),
  ];

  const patientOptions: AutoCompleteOption[] = [
    UNASSIGNED_PATIENT_OPTION,
    ...(patients.suggestions ?? []),
  ];

  const handleConfirm = (): void => {
    for (const filterKey of Object.keys(tempFilters)) {
      filters[filterKey].setter(tempFilters[filterKey]);
    }

    const activeFilters = Object.fromEntries(
      Object.entries(tempFilters ?? {}).map(
        ([filterKey, filterValues]: [
          keyof TTaskFilters,
          AutoCompleteOption[],
        ]) => [
          filterKey,
          filterValues.map(({ value }: AutoCompleteOption) => value),
        ],
      ),
    ) as Record<keyof TTaskFilters, string[]>;

    getTasks(getTaskParamsFromFilters(activeFilters));

    handleClose();
  };

  const taskTitleSearchValue = tempFilters?.title?.[0]?.primaryText ?? '';

  const searchIcon = (
    <Search fill={primary[100]} fillOpacity={1} height="20px" width="20px" />
  );

  return !isOpen ? null : (
    <ChakraMenuList
      height="600px"
      maxWidth="500px"
      overflowY="auto"
      padding="20px"
      width="500px"
      zIndex={3}
    >
      <ChakraSimpleGrid flexDir="column" paddingBottom="60px" spacing="16px">
        <TextInput
          endIcon={
            taskTitleSearchValue && (
              <Close
                cursor="pointer"
                fill={primary[100]}
                height="16px"
                width="16px"
              />
            )
          }
          endIconClick={handleChangeTitleFilter}
          id={`${TOP_LEVEL_ID}__title`}
          label="Title"
          onChange={handleChangeTitleFilter}
          placeholder="Search"
          value={taskTitleSearchValue}
        />
        <AutoComplete
          endIcon={searchIcon}
          fetchOptions={(searchTerm) => {
            getAssigneeSearchProviders({
              'page-number': 1,
              'page-size': GET_PROVIDERS_PAGE_SIZE,
              'provider-search': searchTerm,
              'include-email-search': false,
            });
          }}
          id={`${TOP_LEVEL_ID}__assignee`}
          isDebounced
          isLoading={
            isGetInitialProvidersFetching || isGetAssigneeProvidersFetching
          }
          multiple
          numOptionsNeverFilterOut={2}
          onRemove={handleRemove('assignee')}
          onSelect={handleAdd('assignee')}
          options={assigneeProviderOptions}
          placeholder="Search"
          selected={tempFilters.assignee}
          title="Assignee"
          toggleListOnFocus
          useGenericIconWhen={(option: AutoCompleteOption) =>
            option.value === UNASSIGNED_FILTER_OPTION.value
          }
        />
        <AutoComplete
          endIcon={searchIcon}
          fetchOptions={patients.handleSearch}
          id={`${TOP_LEVEL_ID}__patient`}
          isDebounced
          isInfiniteScrollable
          isLoading={patients.isLoading}
          multiple
          onRemove={handleRemove('patient')}
          onSelect={handleAdd('patient')}
          options={patientOptions}
          placeholder="Search"
          selected={tempFilters.patient}
          shouldFetchOnListOpen
          title="Patient"
          toggleListOnFocus
          totalOptionsCount={patients.totalCount}
          useGenericIconWhen={(option: AutoCompleteOption) =>
            option.value === UNASSIGNED_PATIENT_OPTION.value
          }
        />
        <AutoComplete
          endIcon={searchIcon}
          hideAvatar
          id={`${TOP_LEVEL_ID}__category`}
          isTextInput
          multiple
          onRemove={handleRemove('category')}
          onSelect={handleAdd('category')}
          options={categories}
          placeholder="Search"
          selected={tempFilters.category}
          title="Category"
          toggleListOnFocus
        />
        <MenuSelect
          formLabel="Status"
          formMarginBottom="16px"
          id={`${TOP_LEVEL_ID}__status`}
          listMaxHeight="220px"
          multiple
          onSelect={handleStatusSelect}
          options={taskStatusDefinitions
            .filter(
              ({ title }) =>
                !tempFilters.status.some(
                  ({ primaryText }) => primaryText === title,
                ),
            )
            .map(({ title }) => ({
              node: title,
              value: title,
            }))}
          placeholder="Status"
          selectedValues={tempFilters.status.map(({ primaryText, value }) => ({
            node: primaryText,
            value,
          }))}
        />
        <CheckboxGroup
          direction="row"
          id={`${TOP_LEVEL_ID}__priority`}
          justifyContent
          onChange={handlePrioritiesSelect}
          options={priorities}
          selected={tempFilters.priority.map(({ value }) => value)}
          title="Priority"
        />
        <ChakraFlex direction="column">
          <ChakraText
            color="neutral.secondary"
            fontSize="12px"
            margin="0 0 6px 14px"
          >
            Created date
          </ChakraText>
          <ChakraSimpleGrid columns={2} gap={2}>
            <ChakraFlex direction="column">
              <DatePicker
                id={`${TOP_LEVEL_ID}__created-date-start`}
                maxDate={createdToDate ?? new Date()}
                onSelect={(date) => {
                  handleDateSelect(
                    date,
                    'createdDateFrom',
                    'Created on or after',
                    true,
                  );
                  setCreatedFromDate(date);
                }}
                placeHolderText="MM/DD/YYYY"
                value={getDateValue(
                  tempFilters.createdDateFrom?.[0]?.value ?? '',
                )}
              />
            </ChakraFlex>
            <ChakraFlex direction="column">
              <DatePicker
                id={`${TOP_LEVEL_ID}__created-date-end`}
                maxDate={new Date()}
                minDate={createdFromDate ?? null}
                onSelect={(date) => {
                  handleDateSelect(
                    date,
                    'createdDateTo',
                    'Created on or before',
                    false,
                  );
                  setCreatedToDate(date);
                }}
                placeHolderText="MM/DD/YYYY"
                value={getDateValue(
                  tempFilters.createdDateTo?.[0]?.value ?? '',
                )}
              />
            </ChakraFlex>
          </ChakraSimpleGrid>
        </ChakraFlex>
        <ChakraFlex direction="column">
          <ChakraText
            color="neutral.secondary"
            fontSize="12px"
            margin="0 0 6px 14px"
          >
            Due date
          </ChakraText>
          <ChakraSimpleGrid columns={2} gap={2}>
            <ChakraFlex direction="column">
              <DatePicker
                id={`${TOP_LEVEL_ID}__due-date-start`}
                maxDate={dueToDate ?? null}
                onSelect={(date) => {
                  handleDateSelect(
                    date,
                    'dueDateFrom',
                    'Due on or after',
                    true,
                  );
                  setDueFromDate(date);
                }}
                placeHolderText="MM/DD/YYYY"
                value={getDateValue(tempFilters.dueDateFrom?.[0]?.value ?? '')}
              />
            </ChakraFlex>
            <ChakraFlex direction="column">
              <DatePicker
                id={`${TOP_LEVEL_ID}__due-date-end`}
                minDate={dueFromDate ?? null}
                onSelect={(date) => {
                  handleDateSelect(
                    date,
                    'dueDateTo',
                    'Due on or before',
                    false,
                  );
                  setDueToDate(date);
                }}
                placeHolderText="MM/DD/YYYY"
                value={getDateValue(tempFilters.dueDateTo?.[0]?.value ?? '')}
              />
            </ChakraFlex>
          </ChakraSimpleGrid>
        </ChakraFlex>
        <AutoComplete
          endIcon={searchIcon}
          fetchOptions={(searchTerm) => {
            getCreatorSearchProviders({
              'page-number': 1,
              'page-size': GET_PROVIDERS_PAGE_SIZE,
              'provider-search': searchTerm,
              'include-email-search': false,
            });
          }}
          id={`${TOP_LEVEL_ID}__creator`}
          isDebounced
          isLoading={
            isGetInitialProvidersFetching || isGetCreatorProvidersFetching
          }
          multiple
          numOptionsNeverFilterOut={1}
          onRemove={handleRemove('creator')}
          onSelect={handleAdd('creator')}
          options={creatorProviderOptions}
          placeholder="Search"
          selected={tempFilters.creator}
          title="Creator"
          toggleListOnFocus
        />
      </ChakraSimpleGrid>
      <ChakraBox
        background="neutral.background.primary"
        bottom="1px" // avoid covering the menu border
        padding="12px 0"
        position="absolute"
        width="458px"
        zIndex={2}
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__apply`}
          isFullWidth
          onClick={handleConfirm}
        >
          Apply
        </FireflyButton>
      </ChakraBox>
    </ChakraMenuList>
  );
};

export default TaskFilterMenu;
